import React, { useEffect, useRef, useMemo } from "react";

import useTranslations from "../../../../hooks/useTranslations";

import { Button, VStack } from "@chakra-ui/react";
import { Textarea, Radio, RadioGroup, Input } from "../../../wombat-ui";

import {
  getAnswersByRole,
  QuestionImplProps,
  QuestionPreviewImplProps,
} from "../Question";
import QuestionTitle from "./utils/QuestionTitle";
import Answer from "./utils/Answer";
import useViewer from "../../../../hooks/useViewer";
import { QuestionFragment, AnswerFragment } from '../../../../graphql/generated/definitions';
import QuestionDocuments from "./utils/QuestionDocuments";

type QuestionEvaluationOptions = {
  value: string;
  label: string;
};

type QuestionEvaluationValue = {
  selector: string;
  comment: string;
  timeSpent: number;
};

type QuestionEvaluation = {
  options: QuestionEvaluationOptions[];
  defaultValue: QuestionEvaluationValue;
} & QuestionFragment;

export function isQuestionEvaluation(
  q: QuestionFragment
): q is QuestionEvaluation {
  return q.type === "QuestionEvaluation";
}

export function parseQuestion(question: QuestionFragment): QuestionEvaluation {
  return question as QuestionEvaluation;
}

type AnswerEvaluation = {
  value: QuestionEvaluationValue;
} & AnswerFragment;

export function parseAnswers(answers: AnswerFragment[]): AnswerEvaluation[] {
  return answers as AnswerEvaluation[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface QuestionEvaluationProps extends QuestionImplProps {}

export const QuestionEvaluation: React.FC<QuestionEvaluationProps> = React.memo(
  ({
    question: rawQuestion,
    answers: rawAnswers,
    review,
    register,
    errors,
    toggleHistoryModalOpen,
    status,
    displayIndex,
    name,
    needsToBeFilled,
    onReview,
    autoFocus,
  }) => {
    const t = useTranslations();

    const { id: viewerId } = useViewer();
    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);
    const answers = useMemo(
      () => getAnswersByRole(rawAnswers, parseAnswers, review, viewerId),
      [rawAnswers, viewerId]
    );

    const firstRadioRef = useRef(null);
    useEffect(() => {
      if (autoFocus) firstRadioRef.current?.focus();
    }, [firstRadioRef.current, autoFocus]);

    return (
      <div>
        <RadioGroup
          display="row"
          title={
            <QuestionTitle
              title={question.label}
              required={question.required && status === "to_be_filled_collaborator"}
              description={question.description}
              displayIndex={displayIndex}
              referents={
                question.visibility === "collaborator" ? [] : review.referents
              }
            />
          }
          description={
            <VStack align="flex-start">
              <QuestionDocuments documents={question.documents} />
              <Button
                onClick={toggleHistoryModalOpen}
                variant="link"
                size="xs"
                id={`answerHistoryButton-${question.id}`}
              >
                {t("reports.answerHistory")}
              </Button>
            </VStack>
          }
        >
          {question.options.map((opt, index) => (
            <Radio
              defaultChecked={answers?.mine?.[0]?.value?.selector === opt.value}
              key={opt.value}
              className={
                opt?.isGoal ? "text-warning-500 bg-warning-100" : undefined
              }
              name={`${name}.value.selector`}
              help={
                opt?.isGoal ? (t("templates.targetLevel") as string) : undefined
              }
              label={`${opt.label}`}
              id={`${question.label}_${opt.value}`}
              value={opt.value}
              disabled={!needsToBeFilled}
              variant="boxed"
              error={errors}
              ref={(ref) => {
                if (index === 0) firstRadioRef.current = ref;
                register(ref, {
                  required: question.required && status === "to_be_filled_collaborator"
                    ? t("forms.errors.required") as string
                    : false,
                });
              }}
            />
          ))}
        </RadioGroup>
        {question.timeTracked && needsToBeFilled && (
          <Input
            label={
              !question.expectedTimeValue
                ? (t("reports.timeSpentInHour") as string)
                : `${t("reports.timeSpentInHour") as string} - ${t(
                    `reports.expectedTime.${question.expectedTimeUnit}`,
                    {
                      count: question.expectedTimeValue,
                    }
                  )}`
            }
            type="number"
            size="small"
            min={0}
            disabled={!needsToBeFilled}
            id={`${question.id}-timeTracked`}
            name={`${name}.timeSpent`}
            ref={register({ valueAsNumber: true })}
            defaultValue={answers?.collaborator?.[0]?.timeSpent || undefined}
          />
        )}
        {needsToBeFilled && (
          <div className="max-w-2xl pt-4">
            <Textarea
              defaultValue={answers?.mine?.[0]?.value?.comment}
              ref={register()}
              name={`${name}.value.comment`}
              label={t("reports.comments") as string}
              id={question.id}
              rows={2}
              help={t("reports.commentsHelp") as string}
            />
          </div>
        )}
        {(status === "to_be_filled_evaluator" || onReview) && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.collaboratorAnswer")}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.collaborator.length > 0 ? (
                answers.collaborator.map((a) => (
                  <Answer
                    answer={a}
                    withComment
                    comment={a.value.comment}
                    key={a.id}
                  />
                ))
              ) : (
                <p className="text-xs">{t("reports.noAnswer")}</p>
              )}
            </div>
          </div>
        )}
        {answers.referents.length > 0 && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.evaluatorsAnswers", {
                count: answers.referents.length,
              })}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.referents.length > 0 ? (
                answers.referents.map((a) => (
                  <Answer
                    answer={a}
                    withComment
                    comment={a.value.comment}
                    key={a.id}
                  />
                ))
              ) : (
                <p className="text-xs">{t("reports.noAnswer")}</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

type QuestionEvaluationPreviewProps = QuestionPreviewImplProps;

export const QuestionEvaluationPreview: React.FC<QuestionEvaluationPreviewProps> =
  React.memo(({ question: rawQuestion, displayIndex }) => {
    const t = useTranslations();

    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);

    return (
      <div>
        <RadioGroup
          title={
            <QuestionTitle title={question.label} displayIndex={displayIndex} />
          }
          description={
            t("reports.evaluationQuestion", {
              maxRankIndex: question.options.length,
            }) as string
          }
        >
          {question.options.map((opt, index) => (
            <Radio
              key={`${opt.value}`}
              label={`${opt.label} (${index + 1})`}
              id={`${question.label}_${opt.value}`}
              disabled
              variant="boxed"
            />
          ))}
        </RadioGroup>
      </div>
    );
  });
